import React, { useRef, useMemo, memo } from "react";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  CustomFormSectionDto,
  highlightMatch,
  useMultistepFormStore,
} from "@components/common/MultistepForm";
import { Box, Typography, Chip } from "@components/base";

interface StepLabel {
  section: CustomFormSectionDto;
  active: boolean;
}

const StepLabel: React.FC<StepLabel> = memo(
  ({ section: { name, isRequired, isCompleted }, active }) => {
    const { t } = useTranslation();
    const theme = useTheme<Theme>();
    const md = useMediaQuery(theme.breakpoints.down("md"));
    const labelRef = useRef<HTMLDivElement>(null);
    const { searchQuery } = useMultistepFormStore();

    const highlightedTruncatedLabel = useMemo(
      () => highlightMatch(name, searchQuery),
      [name, searchQuery],
    );

    return (
      <Box
        ref={labelRef}
        display="flex"
        flexGrow={1}
        justifyContent="space-between"
        alignItems={md ? "center" : "flex-start"}
        flexDirection={md ? "column-reverse" : "row"}
      >
        <Box
          sx={{
            height: md ? "auto" : "64px",
            marginRight: md ? 0 : 2,
            paddingTop: md ? 0 : "4px",
          }}
        >
          <Typography
            variant="body2"
            color="textPrimary"
            sx={{
              fontWeight: active ? "bold" : "normal",
              fontSize: md ? 10 : 14,
            }}
          >
            {highlightedTruncatedLabel}
          </Typography>
        </Box>
        {(isRequired || md) && (
          <Chip
            variant="filled"
            label={
              isCompleted
                ? t("multistep.stepperStatus.completed")
                : t("multistep.stepperStatus.required")
            }
            size="medium"
            color={isCompleted ? "success" : "warning"}
          />
        )}
      </Box>
    );
  },
);

export default StepLabel;
